import { Input, Radio, Select } from 'antd';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { PresetFormValues } from '../../../lib/types';
import { BUTTON_THEMES, FONT_FAMILIES, ICON_TYPES } from '@modules/presets/lib/constants';
import { ColorField } from '../color-field';
import { PresetFormItem } from '../form-item';
import styles from '../preset-form.module.scss';

interface AppearanceSettingsProps {
  control: Control<PresetFormValues>;
  errors: FieldErrors<PresetFormValues>;
  disabled?: boolean;
}

export const AppearanceSettings = ({ control, errors, disabled }: AppearanceSettingsProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'presets.editor.appearance' });
  const buttonTheme = useWatch({ control, name: 'appearance.button_theme' });
  const iconType = useWatch({ control, name: 'appearance.widget_icon.type' });

  return (
    <div className={styles.fieldsGrid}>
      <PresetFormItem label={t('brandColor')} error={errors.appearance?.brand_color?.message}>
        <Controller
          control={control}
          name="appearance.brand_color"
          render={({ field }) => (
            <ColorField value={field.value} disabled={disabled} onChange={field.onChange} />
          )}
        />
      </PresetFormItem>

      <PresetFormItem label={t('fontFamily')}>
        <Controller
          control={control}
          name="appearance.font_family"
          render={({ field }) => (
            <Select
              value={field.value}
              disabled={disabled}
              placeholder={t('fontFamilyPlaceholder')}
              options={FONT_FAMILIES.map((font) => ({ label: font, value: font }))}
              onBlur={field.onBlur}
              onChange={field.onChange}
            />
          )}
        />
      </PresetFormItem>

      <PresetFormItem label={t('buttonTheme')} fullWidth required>
        <Controller
          control={control}
          name="appearance.button_theme"
          render={({ field }) => (
            <Radio.Group
              {...field}
              disabled={disabled}
              optionType="button"
              buttonStyle="solid"
              options={BUTTON_THEMES.map((theme) => ({
                label: t(`themes.${theme}`),
                value: theme,
              }))}
              className={styles.segmentedControl}
            />
          )}
        />
      </PresetFormItem>

      {buttonTheme === 'custom' && (
        <>
          <PresetFormItem
            label={t('buttonBackgroundColor')}
            error={errors.appearance?.button_background_color?.message}
          >
            <Controller
              control={control}
              name="appearance.button_background_color"
              render={({ field }) => (
                <ColorField value={field.value} disabled={disabled} onChange={field.onChange} />
              )}
            />
          </PresetFormItem>
          <PresetFormItem
            label={t('buttonTextColor')}
            error={errors.appearance?.button_text_color?.message}
          >
            <Controller
              control={control}
              name="appearance.button_text_color"
              render={({ field }) => (
                <ColorField value={field.value} disabled={disabled} onChange={field.onChange} />
              )}
            />
          </PresetFormItem>
        </>
      )}

      <PresetFormItem label={t('iconType')} fullWidth required>
        <Controller
          control={control}
          name="appearance.widget_icon.type"
          render={({ field }) => (
            <Radio.Group
              {...field}
              disabled={disabled}
              optionType="button"
              buttonStyle="solid"
              options={ICON_TYPES.map((type) => ({
                label: t(`iconTypes.${type}`),
                value: type,
              }))}
              className={styles.segmentedControl}
            />
          )}
        />
      </PresetFormItem>

      {iconType === 'custom' && (
        <PresetFormItem
          label={t('customIconUrl')}
          error={errors.appearance?.widget_icon?.custom_url?.message}
          fullWidth
          required
        >
          <Controller
            control={control}
            name="appearance.widget_icon.custom_url"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                disabled={disabled}
                placeholder={t('customIconUrlPlaceholder')}
              />
            )}
          />
        </PresetFormItem>
      )}
    </div>
  );
};
